import React from 'react';
import pt from 'prop-types';

import { countryOptions, stateOptions, countryStateLabel } from 'utils/geo';

import { BUDGET_OPTIONS } from 'partners/shared/budget';
import {
  PARTNER_CAPABILITIES,
  arrayCapabilitiesToObject,
} from 'partners/shared/capabilities';

import Modal from 'components/modal';
import Icon from 'components/icon';
import { Form, Field } from 'components/form';

import PartnerCurrencies from 'partners/components/pages/settings/partner-currencies';
import becomePartnerCover from './become-partner-cover.png';
import becomePartnerHelpIcon from './become-partner-help-icon.svg';

import './BecomePartner.scss';

const modalTitle = (
  <>
    Become a partner{' '}
    <a
      href="https://www.swell.is/become-a-partner"
      target="_blank"
      rel="noreferrer"
      className="become-partner-help-link"
    >
      <img src={becomePartnerHelpIcon} alt="" width={20} height={20} />
    </a>
  </>
);

export default class BecomePartnerModal extends React.PureComponent {
  static propTypes = {
    onSubmitApplication: pt.func,
  };

  static contextTypes = {
    closeModal: pt.func.isRequired,
    user: pt.object.isRequired,
    router: pt.object,
  };

  state = {
    step: 'promo', // 'promo' | 'form' | 'agreement'
    agreed: false,
    values: {},
  };

  onClickApply = () => {
    this.setState({ step: 'form' });
  };

  onClickViewAgreement = (e) => {
    e.preventDefault();
    this.setState({ step: 'agreement' });
  };

  onClickBack = () => {
    this.setState({ step: 'form' });
  };

  onClickAcceptAgreement = () => {
    this.setState({ step: 'form', agreed: true });
  };

  onChangeForm = (values) => {
    this.setState({ values });
  };

  onSubmitForm = async (values) => {
    const { onSubmitApplication } = this.props;
    if (onSubmitApplication) {
      await onSubmitApplication({
        ...values,
        application: {
          ...values.application,
          capabilities: arrayCapabilitiesToObject(
            values.application?.capabilities || [],
          ),
        },
      });
    }
    this.setState({ step: 'confirmation' });
  };

  onClickGoToProfile = () => {
    const { closeModal, router } = this.context;
    closeModal();
    if (router) {
      router.push('/partner-page');
    }
  };

  renderPromo() {
    return (
      <>
        <div className="become-partner-cover">
          <img
            src={becomePartnerCover}
            alt=""
            className="become-partner-cover-img"
          />
        </div>

        <div className="become-partner-body">
          <p className="become-partner-description">
            As a Partner, you can charge for the apps you publish and earn
            revenue through Swell. Verified partners set their own prices, with
            Swell handling billing and payouts. Partners also get a public
            profile in the marketplace directory.
          </p>

          <button
            type="button"
            className="become-partner-apply-btn"
            onClick={this.onClickApply}
          >
            Apply now
          </button>
        </div>
      </>
    );
  }

  renderForm() {
    const { agreed, values } = this.state;
    const country = values?.application?.country;

    return (
      <Form
        key={`form-${agreed}`}
        onSubmit={this.onSubmitForm}
        onChange={this.onChangeForm}
        values={values}
      >
        <p className="become-partner-form-subtitle">
          Enter your company details below
        </p>

        <div className="become-partner-form">
          <div className="row">
            <Field
              type="text"
              name="application.company_name"
              label="Company name"
              defaultValue={values?.application?.company_name}
              className="span4"
              required
            />
          </div>

          <div className="row">
            <Field
              type="text"
              name="application.email"
              label="Email"
              defaultValue={values?.application?.email}
              rules="required, email"
              className="span2"
            />

            <Field
              type="text"
              name="application.website"
              label="Website"
              defaultValue={values?.application?.website}
              rules="url"
              className="span2"
            />
          </div>

          <div className="row">
            <Field
              type="text"
              name="application.city"
              label="City"
              defaultValue={values?.application?.city}
              className="span2"
            />

            <Field
              type="text"
              name="application.zip"
              label="Zip / Postal code"
              defaultValue={values?.application?.zip}
              className="span2"
            />
          </div>

          <div className="row">
            <Field
              type="select"
              name="application.country"
              label="Country"
              defaultValue={values?.application?.country}
              options={countryOptions}
              className="span2"
            />

            <Field
              type={stateOptions[country] ? 'select' : 'text'}
              name="application.state"
              label={countryStateLabel(country) || 'State'}
              defaultValue={values?.application?.state}
              options={stateOptions[country]}
              className="span2"
            />
          </div>

          <div className="row">
            <div className="span4">
              <PartnerCurrencies
                defaultValue={values?.application?.currencies || []}
              />
            </div>
          </div>

          <div className="row">
            <Field
              type="select"
              name="application.budget"
              label="Typical budget"
              className="span4"
              options={BUDGET_OPTIONS}
              required={true}
              defaultValue={values?.application?.budget}
            />
          </div>

          <div className="row">
            <div>
              <div
                id="capability-checklist"
                className="settings-capability-checklist"
              >
                <div className="form-field-label">Expertise</div>

                <div className="form-field-hint mt-0">
                  Select all that apply to your business
                </div>
              </div>

              <Field
                type="checkbox"
                name="application.capabilities"
                aria-labelledby="capability-checklist"
                options={PARTNER_CAPABILITIES}
                defaultValue={values?.application?.capabilities}
                stacked
              />
            </div>
          </div>

          <div className="row">
            <div className="span4">
              <Field
                type="checkbox"
                name="application.agreed"
                required
                defaultChecked={agreed}
                label={
                  <>
                    I&apos;ve read and agree to the{' '}
                    <a
                      href="#"
                      className="become-partner-agreement-link"
                      onClick={this.onClickViewAgreement}
                    >
                      Partner Agreement
                    </a>
                    .
                  </>
                }
              />
            </div>
          </div>

          <div className="row">
            <button type="submit" className="become-partner-apply-btn span4">
              Submit
            </button>
          </div>
        </div>
      </Form>
    );
  }

  renderConfirmation() {
    return (
      <>
        <p className="become-partner-submitted-text">
          Your application has been submitted! We&apos;ll be in touch soon.
        </p>

        <div className="become-partner-cover become-partner-cover--confirmation">
          <img
            src={becomePartnerCover}
            alt=""
            className="become-partner-cover-img"
          />
        </div>

        <button
          type="button"
          className="become-partner-apply-btn"
          onClick={this.onClickGoToProfile}
        >
          Go to creator profile
        </button>
      </>
    );
  }

  renderAgreement() {
    return (
      <>
        <button
          type="button"
          className="become-partner-back-btn"
          onClick={this.onClickBack}
        >
          <Icon fa="arrow-left" />
          Back
        </button>
        <h4 className="become-partner-agreement-title">Partner agreement</h4>
        <div className="become-partner-agreement-content">
          <p className="become-partner-agreement-heading">
            Partnership &lt;Agency or Technology&gt; Agreement
          </p>
          <p>
            Lorem Ipsum is simply dummy text of the printing and typesetting
            industry. Lorem Ipsum has been the industry&apos;s standard dummy
            text ever since the 1500s, when an unknown printer took a galley of
            type and scrambled it to make a type specimen book. It has survived
            not only five centuries, but also the leap into electronic
            typesetting, remaining essentially unchanged. It was popularised in
            the 1960s with the release of Letraset sheets containing Lorem Ipsum
            passages, and more recently with desktop publishing software like
            Aldus PageMaker including versions of Lorem Ipsum.
          </p>
          <p className="become-partner-agreement-heading">
            Partnership &lt;Agency or Technology&gt; Agreement
          </p>
        </div>
        <div className="become-partner-agreement-footer">
          <a
            href="https://www.swell.is/partner-agreement.pdf"
            target="_blank"
            rel="noreferrer"
            className="become-partner-download-link"
          >
            <Icon fa="file-alt" />
            Download
          </a>
          <button
            type="button"
            className="become-partner-apply-btn"
            onClick={this.onClickAcceptAgreement}
          >
            Accept and continue
          </button>
        </div>
      </>
    );
  }

  render() {
    const { step } = this.state;

    return (
      <Modal
        title={step === 'agreement' ? null : modalTitle}
        helpLink={false}
        width={520}
        cancel={false}
        actions={false}
        className="become-partner-modal"
      >
        {step === 'promo' && this.renderPromo()}
        {step === 'form' && this.renderForm()}
        {step === 'agreement' && this.renderAgreement()}
        {step === 'confirmation' && this.renderConfirmation()}
      </Modal>
    );
  }
}
